import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useThemeStore } from "../../store/theme";
import { useNavigationState } from "../../hooks/useNavigationState";
import {
  HeartHandshake,
  Lightbulb,
  Settings,
  User,
  Search,
  Sparkle,
  Banknote,
  HelpCircle,
  CheckSquare,
  HandCoinsIcon,
  LogOut,
  Globe,
  Loader2,
} from "lucide-react";

// Mock or existing app store hooks if you only need isLoggedIn/logout here
import { useAppStore } from "../../store";

const LeftSidebar: React.FC = () => {
  const { colors, theme } = useThemeStore();
  const { isLoggedIn, logout } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigating = useNavigationState();

  // Local state for detected region and loading status
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const [locating, setLocating] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  // Self-contained location detection logic
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (!navigator.geolocation) return;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();

          if (data?.address) {
            const city =
              data.address.town ||
              data.address.city ||
              data.address.village ||
              data.address.suburb ||
              "";
            const state = data.address.state || data.address.county || "";
            const country = data.address.country || "";

            const regionString = [city, state, country]
              .filter(Boolean)
              .join(", ");

            if (regionString) {
              setDetectedRegion(regionString);
            }
          }
        } catch (err) {
          console.error("Reverse geocoding error:", err);
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        console.warn("Geolocation error:", error.message);
        setLocating(false);
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  const navigationItems = [
    { path: "/", icon: Search, label: "Discover", exact: true },
    { path: "/following", icon: HeartHandshake, label: "Following" },
    { path: "/hub", icon: Lightbulb, label: "Idea Hub" },
    { path: "/llm", icon: Sparkle, label: "Spark" },
    { path: "/funding", icon: Banknote, label: "Funding" },
    { path: "/investors", icon: HandCoinsIcon, label: "Investors" },
    { path: "/checklists", icon: CheckSquare, label: "Checklists" },
    ...(isLoggedIn ? [{ path: "/profile", icon: User, label: "Profile" }] : []),
    { path: "/settings", icon: Settings, label: "Settings" },
    { path: "/help", icon: HelpCircle, label: "Help" },
  ];

  const handleNavigation = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  return (
    <aside
      className="w-64 border-r pt-4 pb-6 px-4 fixed left-0 top-16 h-full overflow-y-auto"
      style={{
        backgroundColor: colors.background,
        borderColor: theme === "light" ? "#e5e7eb" : "#374151",
      }}
    >
      <nav className="space-y-2">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <button
              key={item.path}
              id={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => handleNavigation(item.path)}
              className={`idearify-nav-item ${isActive ? "idearify-nav-item active" : ""} ${isNavigating ? "opacity-50" : ""}`}
              style={{
                color: isActive ? colors.primary : colors.text,
                backgroundColor: "transparent",
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
              disabled={isNavigating}
            >
              <Icon
                size={24}
                style={{ color: isActive ? colors.primary : colors.gray }}
              />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Divider */}
      <div
        className="border-t my-6"
        style={{ borderColor: theme === "light" ? "#e5e7eb" : "#374151" }}
      ></div>

      {/* Additional Links */}
      <div className="space-y-2">
        <a href="https://idearify.com/company" className="idearify-nav-item" style={{ color: colors.gray }}>
          <span className="text-xs">About</span>
        </a>
        <a href="https://idearify.com/help" className="idearify-nav-item" style={{ color: colors.gray }}>
          <span className="text-xs">Contact</span>
        </a>
      </div>

      {/* Region & Log Out Container */}
      <div className="mt-8 mb-4 space-y-2">
        {/* State 1: Locating */}
        {locating && (
          <div
            className="flex items-center gap-2 px-3 py-2 text-xs rounded-md"
            style={{ color: colors.gray }}
          >
            <Loader2 size={16} className="animate-spin" />
            <span>Detecting region...</span>
          </div>
        )}

        {/* State 2: Detected */}
        {!locating && detectedRegion && (
          <div
            className="flex items-center gap-2 px-3 py-2 text-xs rounded-md"
            style={{ color: colors.gray }}
            title={detectedRegion}
          >
            <Globe size={16} className="shrink-0" />
            <span className="truncate">{detectedRegion}</span>
          </div>
        )}

        {/* Log Out Button */}
        {isLoggedIn && (
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="idearify-nav-item"
            style={{
              color: colors.error || "#ef4444",
              backgroundColor: "transparent",
              border: "none",
              width: "100%",
              textAlign: "left",
            }}
          >
            <LogOut size={24} style={{ color: colors.error || "#ef4444" }} />
            <span className="text-sm font-medium">Log Out</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default LeftSidebar;